import {useQuery} from "@apollo/client";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {RequiredDateRange} from "@/components/datetime/utils.ts";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {useFilteredRowSelection} from "@/components/table/hooks/useFilteredRowSelection.ts";
import {useTable} from "@/lib/hooks/useTable.ts";
import {GQL_INCIDENTS, IncidentsListResponse} from "@/lib/queries/statuspages.ts";
import {Incident} from "@/lib/types/statuspages.ts";
import {IncidentsControlBar} from "@/pages/incidents/IncidentsControlBar.tsx";
import {IncidentsSummaryBar} from "@/pages/incidents/IncidentsSummaryBar.tsx";
import {IncidentsTable} from "@/pages/incidents/IncidentsTable.tsx";
import {BulkDeleteIncidentsActionButtonWithConfirmation} from "@/pages/statuspages/tabs/incidents/actions/BulkDeleteIncidentsActionButtonWithConfirmation.tsx";

export const INCIDENTS_FILTERS_STORAGE_KEY = "incidents_list_filters";

interface IncidentsListProps {
  visibleRange: RequiredDateRange | null;
}

export const IncidentsList = ({visibleRange}: IncidentsListProps) => {
  const {user} = useCurrentUserContext();
  const {
    search,
    searchDebounced,
    searchChanged,
    pagination,
    setPagination,
    sorting,
    setSorting,
    sortingString,
    filterSets,
    setFilterSets,
  } = useTable({defaultSort: "end_compound", sessionStorageKey: INCIDENTS_FILTERS_STORAGE_KEY});

  const {data, previousData} = useQuery<IncidentsListResponse>(GQL_INCIDENTS, {
    variables: {
      ...pagination,
      sort: sortingString,
      search: searchDebounced,
      filters: filterSets,
      visible_range: visibleRange,
    },
  });

  const renderData = data?.response || previousData?.response;

  const {rowSelection, setRowSelection} = useFilteredRowSelection<Incident>({
    data: renderData?.data ?? [],
    getRowId: (incident) => incident.id.toString(),
  });

  if (!renderData) return <LoadingContainer text="Loading Incidents…" />;

  return (
    <>
      <IncidentsSummaryBar stats={renderData.stats} filterSets={filterSets} onFilterSetChange={setFilterSets} />
      <IncidentsControlBar
        search={search}
        setSearch={searchChanged}
        filterSets={filterSets}
        onFilterSetChange={setFilterSets}
      />
      <IncidentsTable
        data={renderData}
        sortColumn={sorting.column}
        sortDirection={sorting.direction}
        onSortingChange={setSorting}
        pagination={pagination}
        setPagination={setPagination}
        isFiltered={!!searchDebounced}
        rowSelection={rowSelection}
        setRowSelection={user?.has_write_access ? setRowSelection : undefined}
        actionBar={
          user?.has_write_access && (
            <BulkDeleteIncidentsActionButtonWithConfirmation
              selectedItemsIds={Object.keys(rowSelection).map((id) => parseInt(id))}
              onSuccess={() => setRowSelection({})}
            />
          )
        }
      />
    </>
  );
};
